import React, { useState } from 'react';
import { Alert, Progress, Button, Space, Flex, Typography } from 'antd';
import { DownloadOutlined, UploadOutlined, CloseOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useExportImportProgress } from '../../../Contexts/ExportImportProgressContext';
import { resolveBackendMessage } from '../../../Utils/apiMessages';

const { Text } = Typography;

const ProgressBar = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { tasks, removeTask } = useExportImportProgress();
    const [isCollapsed, setIsCollapsed] = useState(false);

    if (tasks.length === 0) {
        return null;
    }

    const handleDownload = (task) => {
        if (task.downloadUrl) {
            window.open(task.downloadUrl, '_blank');
        }
    };

    const handleViewAll = () => {
        navigate('/data-exports');
    };

    const getTaskIcon = (task) => {
        if (task.type === 'export') {
            return <DownloadOutlined />;
        }
        return <UploadOutlined />;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'failed':
                return 'error';
            case 'processing':
            default:
                return 'info';
        }
    };

    const getStatusText = (task) => {
        if (task.statusObj && typeof task.statusObj === 'object') {
            return resolveBackendMessage(t, task.statusObj);
        }
        return t(task.status);
    };

    return (
        <div
            style={{
                position: 'sticky',
                top: 64,
                zIndex: 999,
                backgroundColor: '#fff',
                borderBottom: '1px solid #f0f0f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
        >
            <div style={{ padding: '8px 24px' }}>
                <Flex justify="space-between" align="center" style={{ marginBottom: isCollapsed ? 0 : 8 }}>
                    <Space>
                        <Text strong>
                            {t('active_operations')} ({tasks.length})
                        </Text>
                        <Button
                            type="text"
                            size="small"
                            icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
                            onClick={() => setIsCollapsed(!isCollapsed)}
                        />
                    </Space>
                    <Button type="link" size="small" onClick={handleViewAll}>
                        {t('view_all')}
                    </Button>
                </Flex>

                {!isCollapsed && (
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                        {tasks.map(task => (
                            <Alert
                                key={task.id}
                                type={getStatusColor(task.status)}
                                message={
                                    <Flex justify="space-between" align="center" gap="middle">
                                        <Space style={{ flex: 1 }}>
                                            {getTaskIcon(task)}
                                            <Text strong>{task.name}</Text>
                                            <Text type="secondary">
                                                {getStatusText(task)}
                                            </Text>
                                        </Space>

                                        {task.status === 'processing' && (
                                            <div style={{ minWidth: 200 }}>
                                                <Progress
                                                    percent={Math.round(task.progress || 0)}
                                                    size="small"
                                                    status="active"
                                                />
                                            </div>
                                        )}

                                        <Space>
                                            {task.status === 'completed' && task.downloadUrl && (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => handleDownload(task)}
                                                >
                                                    {t('download')}
                                                </Button>
                                            )}
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<CloseOutlined />}
                                                onClick={() => removeTask(task.id)}
                                            />
                                        </Space>
                                    </Flex>
                                }
                                banner
                                closable={false}
                                style={{ marginBottom: 0 }}
                            />
                        ))}
                    </Space>
                )}
            </div>
        </div>
    );
};

export default ProgressBar;
